// Shared helpers for form-level dictation (IP Case Sheet, Emergency).
//
// Every dictated form uses ONE VoiceFormFiller ("🎙 Dictate …") at the top
// of the form rather than a mic per input (see CLAUDE.md, IP Case Sheet →
// "Voice dictation is one control per section"). The filler hands back the
// raw JSON from /api/ai/extract-fields, which the server validates for SHAPE
// but not for VALUE: a select field's answer is "a string", not necessarily
// one of the options this form offers, and a number field's answer is "a
// string" that may still carry a unit ("38.2 degrees"). These helpers are the
// one place each section reads a dictated value back into its own form state,
// so every section applies the same rules:
//
//  - Nothing dictated is ever written straight to the server. It lands in the
//    form for the user to read, correct and save — same "AI proposes, the
//    clinician approves" rule as everywhere else AI touches clinical data.
//  - A value that isn't one of the field's allowed options is dropped, never
//    guessed at.
//  - Free text is APPENDED to what is already in a field, never replacing it,
//    so a second dictation can't silently wipe the first one (or something
//    typed by hand).
import type { VoiceField } from '@/components/VoiceFormFiller';

export type Extracted = Record<string, unknown>;

/** A trimmed, non-empty string answer for `key`, else undefined. */
export function vText(x: Extracted, key: string): string | undefined {
  const v = x[key];
  if (typeof v !== 'string') return undefined;
  const t = v.trim();
  return t ? t : undefined;
}

/** A finite number answer for `key` — tolerates a unit or stray words the
 *  model left in ("38.2 °C", "about 90"). Undefined if no number is present. */
export function vNum(x: Extracted, key: string): number | undefined {
  const t = vText(x, key);
  if (!t) return undefined;
  const m = t.replace(/,/g, '').match(/-?\d+(\.\d+)?/);
  if (!m) return undefined;
  const n = Number(m[0]);
  return Number.isFinite(n) ? n : undefined;
}

/** One of `options` (case-insensitive match), else undefined. */
export function vOneOf<T extends string>(x: Extracted, key: string, options: readonly T[]): T | undefined {
  const t = vText(x, key)?.toLowerCase();
  if (!t) return undefined;
  return options.find(o => o.toLowerCase() === t);
}

/** yes/no answer as a boolean, else undefined. */
export function vBool(x: Extracted, key: string): boolean | undefined {
  const t = vOneOf(x, key, ['yes', 'no'] as const);
  return t === undefined ? undefined : t === 'yes';
}

/** A choice-matrix answer: row key → one of that row's options. */
export function vMatrix(x: Extracted, key: string): Record<string, string> {
  const v = x[key];
  if (!v || typeof v !== 'object' || Array.isArray(v)) return {};
  const out: Record<string, string> = {};
  for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
    if (typeof val === 'string' && val) out[k] = val;
  }
  return out;
}

/** Append dictated text to what's already in a field, never overwrite it. */
export function appendText(current: string | null | undefined, incoming: string | undefined, sep = ' '): string {
  const cur = (current ?? '').trim();
  if (!incoming) return current ?? '';
  if (!cur) return incoming;
  if (cur.toLowerCase().includes(incoming.toLowerCase())) return cur;
  return `${cur}${sep}${incoming}`;
}

/**
 * Merge every free-text key in `keys` from `x` into `current`, appending.
 * Returns only the keys that actually changed, for a `{...prev, ...patch}`.
 */
export function mergeTextFields<T extends object>(current: T, x: Extracted, keys: readonly (keyof T & string)[], sep = '\n'): Partial<T> {
  const patch: Partial<T> = {};
  for (const k of keys) {
    const incoming = vText(x, k);
    if (!incoming) continue;
    (patch as Record<string, unknown>)[k] = appendText((current as Record<string, unknown>)[k] as string | undefined, incoming, sep);
  }
  return patch;
}

// Small builders, so each section's field list reads as a table.
export const textField = (key: string, label: string): VoiceField => ({ key, label, type: 'text' });
export const numField = (key: string, label: string): VoiceField => ({ key, label, type: 'number' });
export const selectField = (key: string, label: string, options: readonly string[]): VoiceField => ({ key, label, type: 'select', options });
export const yesNoField = (key: string, label: string): VoiceField => ({ key, label, type: 'radio', options: ['yes', 'no'] });

/** Dictated temperature to °C: IP and ED vitals are charted in °C, but a nurse may
 *  well say "99.4" — anything above 45 can only be Fahrenheit. */
export function tempToC(n: number): number {
  return n > 45 ? Math.round(((n - 32) * 5) / 9 * 10) / 10 : n;
}

/** A checkbox-group answer: the options mentioned, matched case-insensitively
 *  against the allowed list (anything else dropped). Returns canonical values. */
export function vList<T extends string>(x: Extracted, key: string, options: readonly T[]): T[] {
  const v = x[key];
  if (!Array.isArray(v)) return [];
  const out: T[] = [];
  for (const item of v) {
    if (typeof item !== 'string') continue;
    const hit = options.find(o => o.toLowerCase() === item.trim().toLowerCase());
    if (hit && !out.includes(hit)) out.push(hit);
  }
  return out;
}

/**
 * Roster match for a spoken name — "Dr. Menon" → the one entry whose name
 * contains "menon". Every spoken word (3+ letters, honorific stripped) must
 * appear; an ambiguous or empty match returns undefined so the user picks.
 */
export function matchByName<T>(spoken: string | undefined, items: readonly T[], name: (t: T) => string): T | undefined {
  if (!spoken) return undefined;
  const words = spoken.toLowerCase().replace(/\bdr\.?\s*/g, ' ').split(/[\s.,]+/).filter(w => w.length > 2);
  if (!words.length) return undefined;
  const hits = items.filter(it => words.every(w => name(it).toLowerCase().includes(w)));
  return hits.length === 1 ? hits[0] : undefined;
}

export const listField = (key: string, label: string, options: readonly string[]): VoiceField => ({ key, label, type: 'checkbox-group', options });
